/**
 * Who is reading, on the pages that are not behind the session gate.
 *
 * The landing page and a community's pages are all open to anybody, so they
 * cannot use `useSession` — that hook exists to guarantee a session and
 * redirects without one. Here a signed-out reader is the NORMAL case and gets
 * the whole page; knowing who they are only changes what the account menu
 * says.
 *
 * `null` means signed out, which is why the state is three-valued: `undefined`
 * while the answer is still coming, so the menu does not flash a signed-out
 * face at somebody who is signed in.
 */
import { useEffect, useState } from 'react';
import { auth } from './tomo.js';

export interface Viewer {
  readonly uid: string;
  readonly handle: string | null;
  readonly displayName: string | null;
  readonly avatarUrl: string | null;
}

export type ViewerState = Viewer | null | undefined;

export function useViewer(): ViewerState {
  const [viewer, setViewer] = useState<ViewerState>(undefined);

  useEffect(() => {
    const controller = new AbortController();
    void auth.session(controller.signal).then(
      (session) => {
        if (controller.signal.aborted) return;
        const user = session.user;
        setViewer(user
          ? {
              uid: user.uid,
              handle: user.handle ?? null,
              displayName: user.display_name ?? null,
              avatarUrl: user.avatar_url ?? null,
            }
          : null);
      },
      () => {
        // A 401 here is the ordinary "not signed in", not a failure worth
        // showing anybody. So is a network blip: the page works either way.
        if (!controller.signal.aborted) setViewer(null);
      },
    );
    return () => { controller.abort(); };
  }, []);

  return viewer;
}

/** What to draw in a 26px circle when somebody has no picture. */
export function initialOf(viewer: Viewer): string {
  const name = viewer.displayName ?? viewer.handle ?? '?';
  return name.slice(0, 1).toUpperCase();
}
